import React, { useEffect } from "react";
import { isEmpty, uniq } from "lodash/fp";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { Grid, IconButton, TextField } from "@material-ui/core";
import HorizontalPanel from "../../../components/HorizontalPanel";
import { Toolbar } from "../../../app/components";
import UnitsInput from "../../../components/TextFields/UnitsInput";
import { selectCurrentManualFilters } from "../../../store/filterStore/filterSelectors";
import { useTypedDispatch } from "../../../app/state";
import validationSchema from './manualFilterValidation';
import Multiselect from "../../../components/Multiselect";
import {
  selectDevicesByMapId,
  selectTechnologyConfigurationListByMapId,
  selectWirelessSystemListByMapId
} from "../../../store/deviceStore/deviceSelectors";
import useDevicesPage from "../../DevicesPage/devicePageHooks";
import { selectAllWirelessSystems } from "../../../store/wirelessSystemStore/wirelessSystemSelectors";
import {
  selectAllTechnologyConfigurations
} from "../../../store/technologyConfigurationStore/technologyConfigurationSelectors";
import Chip from "@material-ui/core/Chip";
import {
  setCoordinatesFilter,
  setFilterPanelVisibility,
  setManualFilters,
  setSmartFilterDeviceIdList
} from "../../../store/filterStore/filterState";
import useFilter from "../../../hooks/filter.hook";
import {
  selectLocationIndoorDeviceList,
  selectTechnologyListFromIndoorDevice,
  selectWirelessSystemListFromIndoorDevices
} from "../../../store/mapDeviceStore/mapDeviceSelectors";
import TransitEnterexitIcon from "@material-ui/icons/TransitEnterexit";
import Typography from "@material-ui/core/Typography";
import { Delete, MyLocation } from "@material-ui/icons";
import { POINT_EDIT_TYPE, setEditMode } from "../../../store/generalStore/generalState";

import classes from './index.module.scss';

const FilterPanel = ({ onSelectedDeviceClear, disableSetFilterPoint = false }) => {
  const dispatch = useTypedDispatch();
  const { state } = useDevicesPage();
  const { currentMapId, currentMap } = state;
  const filter = useFilter();
  const { showIndoorDeviceList, smartFilterDeviceIdList, coordinatesFilter } = filter.state;
  const manualFilters = useSelector(selectCurrentManualFilters);
  const fullWirelessSystemList = useSelector(selectAllWirelessSystems);
  const wirelessSystemIdListByMapId = useSelector(selectWirelessSystemListByMapId(currentMapId));
  const fullTechnologyList = useSelector(selectAllTechnologyConfigurations);
  const technologyConfigurationIdByMapId = useSelector(selectTechnologyConfigurationListByMapId(currentMapId));
  const indoorWirelessSystemList = useSelector(selectWirelessSystemListFromIndoorDevices);
  const indoorTechnologyList = useSelector(selectTechnologyListFromIndoorDevice);
  const deviceList = useSelector(selectDevicesByMapId(currentMapId));
  const indoorDeviceList = useSelector(selectLocationIndoorDeviceList(currentMap));
  const wirelessSystemIdList = showIndoorDeviceList ? uniq([ ...wirelessSystemIdListByMapId, ...indoorWirelessSystemList ]) : wirelessSystemIdListByMapId;
  const technologyConfigurationIdList = showIndoorDeviceList ? uniq([ ...technologyConfigurationIdByMapId, ...indoorTechnologyList ]) : technologyConfigurationIdByMapId;
  const fullDeviceList = showIndoorDeviceList ? [ ...deviceList, ...indoorDeviceList ] : deviceList;

  const initialValues = {
    smartFilterDeviceIdList: smartFilterDeviceIdList || [],
    frequencyFrom: manualFilters?.frequencyFrom || '',
    frequencyTo: manualFilters?.frequencyTo || '',
    technologyConfigurationIdList: manualFilters?.technologyConfigurationIdList || [],
    wirelessSystemIdList: manualFilters?.wirelessSystemIdList || [],
    latitude: coordinatesFilter.latitude || '',
    longitude: coordinatesFilter.longitude || '',
    radius: coordinatesFilter.radius || '',
  };

  const filterFormik = useFormik({
    initialValues,
    onSubmit: () => {},
    validationSchema,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false
  });

  useEffect(() => {
    if (coordinatesFilter.latitude) {
      filterFormik.setFieldValue('latitude', coordinatesFilter.latitude);
      filterFormik.setFieldValue('longitude', coordinatesFilter.longitude);
    }
  }, [ coordinatesFilter ]);

  const wirelessSystemsInUse = Multiselect.getSelectedValues(
    fullWirelessSystemList,
    wirelessSystemIdList,
  );

  const wirelessSystemItems = Multiselect.getValues(wirelessSystemsInUse);

  const selectedWirelessSystemList = Multiselect.getSelectedValues(
    fullWirelessSystemList,
    filterFormik.values.wirelessSystemIdList,
  );

  const technologyListInUse = Multiselect.getSelectedValues(
    fullTechnologyList,
    technologyConfigurationIdList,
  );

  const technologyListItems = Multiselect.getValues(technologyListInUse);

  const selectedTechnologyListItems = Multiselect.getSelectedValues(
    fullTechnologyList,
    filterFormik.values.technologyConfigurationIdList,
  );

  const onApply = async () => {
    const valid = await filterFormik.validateForm();
    if (!isEmpty(valid)) {
      return;
    }

    if (filterFormik.values.smartFilterDeviceIdList.length > 0) {
      dispatch(setSmartFilterDeviceIdList(filterFormik.values.smartFilterDeviceIdList));
    } else {
      dispatch(setSmartFilterDeviceIdList(null));
    }
    dispatch(setManualFilters(filterFormik.values));
    dispatch(setCoordinatesFilter({ radius: filterFormik.values.radius }));
    dispatch(setFilterPanelVisibility(null));
    onSelectedDeviceClear();
  }

  const onClear = () => {
    dispatch(setCoordinatesFilter(null));
    dispatch(setManualFilters(null));
    dispatch(setSmartFilterDeviceIdList(null));
  }

  const onCloseClick = () => {
    dispatch(setFilterPanelVisibility(null));
    // clear radius search point if no radius is set
    if (!filterFormik.values.radius) {
      dispatch(setCoordinatesFilter(null));
    }
  }

  const removeFilterPoint = () => {
    dispatch(setCoordinatesFilter(null));
  }

  const setFilterPointOnMap = () => {
    dispatch(setEditMode(POINT_EDIT_TYPE.FILTER));
  }

  return (
    <HorizontalPanel size={HorizontalPanel.SIZES.WHOLE_SPACE}>
      <HorizontalPanel size={HorizontalPanel.SIZES.SMALL}>
        <Toolbar title="Filters">
          <IconButton
            aria-label='close window'
            color='secondary'
            size='small'
            onClick={onCloseClick}
          >
            <TransitEnterexitIcon />
          </IconButton>
        </Toolbar>
      </HorizontalPanel>
      <HorizontalPanel size={HorizontalPanel.SIZES.REST_SPACE}>
        <Grid
          container
          className={classes.container}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Multiselect
                title='Filter by device'
                name='smartFilterDeviceIdList'
                fullWidth
                items={Multiselect.getValues(fullDeviceList)}
                selectedItems={Multiselect.getSelectedValues(fullDeviceList, filterFormik.values.smartFilterDeviceIdList)}
                onChange={(values: number[]) => filterFormik.setFieldValue('smartFilterDeviceIdList', values)}
              />
            </Grid>
            <Grid item xs={6}>
              <UnitsInput
                name="frequencyFrom"
                unitList={UnitsInput.UNIT_TEMPLATE.FREQUENCY}
                label="Frequency from"
                variant='outlined'
                fullWidth
                value={filterFormik.values?.frequencyFrom}
                onChange={filterFormik.handleChange}
                error={Boolean(filterFormik?.errors?.frequencyFrom)}
                helperText={filterFormik?.errors?.frequencyFrom}
              />
            </Grid>
            <Grid item xs={6}>
              <UnitsInput
                name="frequencyTo"
                unitList={UnitsInput.UNIT_TEMPLATE.FREQUENCY}
                label="Frequency to"
                variant='outlined'
                fullWidth
                value={filterFormik.values?.frequencyTo}
                onChange={filterFormik.handleChange}
                error={Boolean(filterFormik?.errors?.frequencyTo)}
                helperText={filterFormik?.errors?.frequencyTo}
              />
            </Grid>
            <Grid item xs={12}>
              <Multiselect
                title='Wireless systems'
                name='wirelessSystemIdList'
                disabled={wirelessSystemItems.length === 0}
                fullWidth
                items={wirelessSystemItems}
                selectedItems={selectedWirelessSystemList}
                onChange={(values: number[]) => filterFormik.setFieldValue('wirelessSystemIdList', values)}
              />
            </Grid>
            <Grid item xs={12}>
              <Multiselect
                title='Technologies'
                name='technologyConfigurationIdList'
                disabled={technologyListItems.length === 0}
                fullWidth
                items={technologyListItems}
                selectedItems={selectedTechnologyListItems}
                onChange={(values: number[]) => filterFormik.setFieldValue('technologyConfigurationIdList', values)}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid
                container
                justifyContent='space-between'
                alignItems='center'
              >
                <Grid item><Typography variant='subtitle1'>Filter point</Typography></Grid>
                <Grid item>
                  {
                    coordinatesFilter.x ? (
                      <IconButton
                        aria-label='remove filter point from map'
                        color='secondary'
                        size='small'
                        onClick={removeFilterPoint}
                      >
                        <Delete />
                      </IconButton>
                    ) : (
                      <IconButton
                        aria-label='set filter point on map'
                        color='secondary'
                        size='small'
                        disabled={disableSetFilterPoint}
                        onClick={setFilterPointOnMap}
                      >
                        <MyLocation />
                      </IconButton>
                    )
                  }
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Latitude'
                variant='outlined'
                name='latitude'
                fullWidth
                disabled
                value={filterFormik.values.latitude}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Longitude'
                variant='outlined'
                name='longitude'
                fullWidth
                disabled
                value={filterFormik.values.longitude}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Radius, m'
                variant='outlined'
                name='radius'
                fullWidth
                disabled={!coordinatesFilter.x}
                value={filterFormik.values.radius}
                onChange={filterFormik.handleChange}
                error={Boolean(filterFormik?.errors?.radius)}
                helperText={filterFormik?.errors?.radius}
              />
            </Grid>
            <Grid item xs={6} container justifyContent='flex-start'>
              <Chip
                label="Apply"
                color={'primary'}
                onClick={onApply}
              />
            </Grid>
            <Grid item xs={6} container justifyContent='flex-end'>
              <Chip
                label="Clear"
                color={'default'}
                onClick={onClear}
              />
            </Grid>
          </Grid>
        </Grid>
      </HorizontalPanel>
    </HorizontalPanel>
  )
}

export default FilterPanel;